'use client'

import { useAtomValue } from 'jotai'
import { WalletHistoryViewed } from '@/src/codegen/ampli'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { useVirtualRows } from '@/src/hooks/use-virtual-rows'
import { useTimeSpanGroupedHistory } from '@/src/queries/hooks/history'
import { accountsAddressesAtom } from '@/src/stores/accounts/atoms'
import { ActivityItem } from './activity-item'
import { EmptyGroupedActivity } from './grouped-activity.empty'
import { ErrorGroupedActivity } from './grouped-activity.error'
import { LoadingGroupedActivity } from './grouped-activity.loading'

export function GroupedActivity() {
  useAmplitudeAnalytics({
    onMountEvent: new WalletHistoryViewed(),
  })

  const accountsAddresses = useAtomValue(accountsAddressesAtom)

  const wallets = accountsAddresses

  const groupedHistoryQuery = useTimeSpanGroupedHistory({
    wallets,
    hideExpired: true,
    enabled: wallets.length > 0,
  })
  const { data: groupedActivity, isLoading, isError } = groupedHistoryQuery

  const { parentRef, rowVirtualizer } = useVirtualRows<
    unknown,
    HTMLButtonElement | HTMLParagraphElement
  >({
    infiniteQuery: groupedHistoryQuery,
    rows: groupedActivity ?? [],
    count: groupedActivity?.length ?? 0,
    paddingEnd: 24,
    estimateSize: () => 80,
    overscan: 0,
  })

  if (isLoading) {
    return <LoadingGroupedActivity />
  }

  if (!groupedActivity || groupedActivity.length === 0) {
    return <EmptyGroupedActivity />
  }

  if (isError) {
    return <ErrorGroupedActivity />
  }

  const activities = rowVirtualizer.getVirtualItems()

  return (
    <section ref={parentRef} className='max-h-full'>
      <div
        className='relative -mx-4'
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
      >
        <div
          className='absolute top-0 left-0 w-full'
          style={{
            transform: `translateY(${activities[0]?.start ?? 0}px)`,
          }}
        >
          {activities.map((virtualRow) => {
            const route = groupedActivity[virtualRow.index]

            /**
             * If we use a flat array we can use dynamic virtualRow,
             * so we can both virtualize headers and activity items,
             * this should allow us to get the best performance
             */
            if (typeof route === 'string') {
              return (
                <p
                  className='text-sm font-normal text-secondary py-3 px-4'
                  ref={rowVirtualizer.measureElement}
                  key={virtualRow.index}
                  data-index={virtualRow.index}
                >
                  {route}
                </p>
              )
            }

            return (
              <ActivityItem
                ref={rowVirtualizer.measureElement}
                key={virtualRow.index}
                data-index={virtualRow.index}
                route={route}
              />
            )
          })}
        </div>
      </div>
    </section>
  )
}
